import { useMemo, useRef, useState } from "react";
import type { InlineTypeWidgetComponentProps } from "@easyblocks/core";
import { DocumentBrowserModal } from "../components/modals/DocumentBrowserModal";
import type { DocumentValue } from "../components/types";
import { getDocumentItemId, getDocumentSelectionLabel } from "../components/utils/document-value";

function getDocumentLabel(value?: DocumentValue | null): string {
  if (!value) return "No document selected";
  return getDocumentSelectionLabel(value) || "No document selected";
}

export function DocumentUploadWidget(props: InlineTypeWidgetComponentProps<DocumentValue>) {
  const selectedValue = (props?.value ?? null) as DocumentValue | null;
  const hasSelection = Boolean(
    getDocumentItemId(selectedValue) ||
      getDocumentSelectionLabel(selectedValue) ||
      selectedValue?.url
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const instanceIdRef = useRef<string>(`documents-browser-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;
  const label = useMemo(() => getDocumentLabel(selectedValue), [selectedValue]);

  return (
    <div className="d-flex flex-column gap-2 w-100 overflow-hidden" style={{ maxWidth: "100%", minWidth: 0 }}>
      <div className="fs-12 text-body-secondary text-wrap text-break" title={label}>
        {label}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center w-100">
        <button
          type="button"
          className="btn btn-sm btn-primary w-100 w-sm-auto"
          onClick={() => setIsModalOpen(true)}
        >
          Browse or Upload
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary w-100 w-sm-auto d-flex align-items-center justify-content-center"
          title="Clear"
          aria-label="Clear"
          onClick={() => props.onChange({})}
          disabled={!hasSelection}
        >
          Remove
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
            className="ms-1"
          >
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v7a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 0A.5.5 0 0 1 9 6v7a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v7a.5.5 0 0 0 1 0V6Z"/>
            <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1 0-2H5.5l.5-.5A1 1 0 0 1 6.707 1h2.586a1 1 0 0 1 .707.293l.5.5H14a1 1 0 0 1 .5 1.207ZM4 4v9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4H4Z"/>
          </svg>
        </button>
      </div>
      <DocumentBrowserModal
        isOpen={isModalOpen}
        instanceId={instanceId}
        onClose={() => setIsModalOpen(false)}
        onPick={(item) => props.onChange(item)}
      />
    </div>
  );
}
